import './index.css'

import { RiDraggable } from '@react-icons/all-files/ri/RiDraggable'
import debounce from 'lodash/debounce'
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { TRACK } from '@disco/constants/ItemTypes'
import {
  clientLibraryFeaturedTracksLoad,
  clientLibraryTracksLoad,
  clientLibraryTracksLoadNext,
  clientLibraryTracksSet,
  clientLibraryTracksUpdate,
} from '@disco/state-management/actions/client-library-track-actions'
import {
  getClientLibrary,
  getClientLibraryTracks,
} from '@disco/state-management/selectors/client-libraries'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'

import type { ClientLibrary } from 'types/client-library'
import type { Track } from 'types/track'

import emptySearch1x from 'assets/images/empty-search.png'
import emptySearch2x from 'assets/images/empty-search@2x.png'
import emptyTracks1x from 'assets/images/empty-tracks.png'
import emptyTracks2x from 'assets/images/empty-tracks@2x.png'

import LibraryMembershipTooltip from 'components/common/library-membership-tooltip'
import Waveform from 'components/common/player/waveform'
import Row from 'components/common/row'
import TrackInfo from 'components/common/track-info'
import DocumentHeadTags from 'components/document-head-tags'
import { Tab, Tabs } from 'components/ui'
import Button from 'components/ui/button'
import Checkbox from 'components/ui/checkbox'
import Draggable from 'components/ui/draggable'
import EmptyState from 'components/ui/empty-state'
import Flex from 'components/ui/flex'
import { HoverProvider } from 'components/ui/hover-provider'
import InfiniteScroll from 'components/ui/infinite-scroll'
import Input from 'components/ui/input'
import PageContainer from 'components/ui/page-container'
import Sortable, { NEW_SORTABLE_ITEM_KEY } from 'components/ui/sortable'
import Tooltip from 'components/ui/tooltip'

import { useDispatch, useSelector } from 'hooks/use-redux'

const LibrariesLibraryTracks = (): React.ReactNode => {
  const dispatch = useDispatch()
  const params = useParams<{ libraryId: string }>()
  const libraryId: number = parseInt(params.libraryId!, 10)
  const library = useSelector(state => getClientLibrary(state, libraryId))
  const tracks = useSelector(state => getClientLibraryTracks(state, libraryId))
  const typeData = useComposerInsteadOfArtist().value
  const [selectedTab, setSelectedTab] = useState(0)
  const [query, setQuery] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoadingNext, setIsLoadingNext] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const debouncedSetSearchTerm = useRef(
    debounce((value: string) => {
      setIsSearching(false)
      setSearchTerm(value)
    }, 300),
  ).current

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel()
    }
  }, [debouncedSetSearchTerm])

  useEffect(() => {
    if (selectedTab === 0) {
      const controller = new AbortController()
      abortControllerRef.current = controller
      dispatch(
        clientLibraryTracksLoad(libraryId, searchTerm, controller.signal),
      )
    }

    if (selectedTab === 1) {
      abortControllerRef.current = null
      dispatch(clientLibraryFeaturedTracksLoad(libraryId, ''))
    }

    setIsLoadingNext(false)

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [dispatch, libraryId, searchTerm, selectedTab])

  const updateSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoadingNext(false)
    setIsSearching(true)
    setQuery(event.target.value)
    debouncedSetSearchTerm(event.target.value)
  }

  const toggleIsFeatured = (track: Track) => () => {
    let add = []
    let remove = []

    if (track['is_featured']) {
      remove.push(track['id'])
    } else {
      add.push(track['id'])
    }

    dispatch(clientLibraryTracksUpdate(libraryId, add, remove))
  }

  const loadNext = (next: string) => {
    setIsLoadingNext(true)
    dispatch(clientLibraryTracksLoadNext(libraryId, next))
  }

  const renderContent = (tracks: {
    loading: boolean
    loaded: boolean
    nextPage: string | null
    list: Track[]
  }) => (!isLoadingNext && tracks.loading ? 'Loading...' : renderTracks(tracks))

  const renderTrackRow = (track: Track, index: number, draggable: boolean) => {
    const tooltip = track['is_featured']
      ? 'Remove from featured'
      : 'Feature this track'
    const cloneTrack = { ...track }

    if (typeData === 'composer') {
      cloneTrack.artist = cloneTrack.metadata.Composer
    }

    return (
      <Row
        key={track['id']}
        className="ClientLibrariesLibraryTracks-row"
        columnsSize={[12]}
        marginBottom={false}
      >
        <div>
          <HoverProvider>
            <Flex spacing="large">
              {draggable && (
                <div className="ClientLibrariesLibraryTracks-dragHandle">
                  <RiDraggable aria-hidden="true" />
                </div>
              )}
              {selectedTab === 1 && (
                <div className="ClientLibrariesLibraryTracks-index">
                  {(index + 1).toString().padStart(2, '0')}
                </div>
              )}
              <Flex.Content>
                <TrackInfo location="explorer" track={track} />
              </Flex.Content>
              <div
                className="ClientLibrariesLibraryTracks-infoIcon"
                draggable={false}
              >
                <LibraryMembershipTooltip
                  libraryId={libraryId}
                  trackId={track['id']}
                />
              </div>
              <div className="ClientLibrariesLibraryTracks-waveform">
                <Waveform
                  className="flex-waveform"
                  track={track}
                  barWidth={1.12}
                  barGap={2.18 - 1.12}
                  noAnimation
                  noSeekClick
                />
              </div>
              <Tooltip content={tooltip}>
                <Checkbox
                  checked={track['is_featured']}
                  onChange={toggleIsFeatured(track)}
                />
              </Tooltip>
            </Flex>
          </HoverProvider>
        </div>
      </Row>
    )
  }

  const handleFeaturedTracksDrop = ({ order }: { order: string[] }) => {
    const trackIds = order
      .filter(key => key !== NEW_SORTABLE_ITEM_KEY)
      .map(key => parseInt(key, 10))
    dispatch(clientLibraryTracksSet(libraryId, trackIds))
  }

  const renderTracks = (tracks: {
    loading: boolean
    loaded: boolean
    nextPage: string | null
    list: Track[]
  }) =>
    tracks.loaded && tracks.list.length === 0 ? (
      renderEmptyState()
    ) : (
      <InfiniteScroll next={tracks.nextPage} loadNext={loadNext}>
        {selectedTab === 1 ? (
          <Sortable
            accept={[TRACK]}
            className="ClientLibrariesLibraryTracks-list"
            id={`featured-tracks-${libraryId}`}
            items={tracks.list}
            onDrop={handleFeaturedTracksDrop}
            renderItem={(track: Track | undefined, index: number) => {
              if (!track) {
                return null
              }

              return (
                <Draggable
                  key={track['id']}
                  id={track['id']}
                  type={TRACK}
                  disableStyles
                  disableContentWrapper
                >
                  {renderTrackRow(track, index, true)}
                </Draggable>
              )
            }}
          />
        ) : (
          <div className="ClientLibrariesLibraryTracks-list">
            {tracks.list.map((track: Track, index: number) =>
              renderTrackRow(track, index, false),
            )}
          </div>
        )}
      </InfiniteScroll>
    )

  const renderEmptyState = () =>
    !isSearching && query === '' ? (
      <EmptyState>
        <img
          srcSet={`${emptyTracks1x}, ${emptyTracks2x} 2x`}
          width="200"
          height="194"
        />
        {selectedTab === 0 && (
          <>
            <p>
              No tracks here yet! To add tracks, select content on the “Content”
              page.
            </p>
            <Button
              appearance="primary"
              caption="Add content"
              link={`/catalogs/${libraryId}/content`}
            />
          </>
        )}
        {selectedTab === 1 && (
          <p>
            No tracks here yet! To add tracks, select them in the “All tracks”
            tab.
          </p>
        )}
      </EmptyState>
    ) : (
      <EmptyState>
        <img
          srcSet={`${emptySearch1x}, ${emptySearch2x} 2x`}
          width="177"
          height="160"
        />
        <p>
          Sorry! We couldn’t find any tracks for that search. Try another query,
          or to add more tracks, add more content in the “Content” page.
        </p>
        <Button
          appearance="primary"
          caption="Add content"
          link={`/catalogs/${libraryId}/content`}
        />
      </EmptyState>
    )

  return (
    <PageContainer.Section
      title="Tracks"
      description="Here you can select the tracks you’d like featured on your Catalog home page. Search through the tracks you’ve chosen to include in the Catalog and select using the checkbox."
    >
      {library && !library['loading'] && (
        <DocumentHeadTags title={`${library['name']} | Tracks | DISCO`} />
      )}
      <Tabs
        className="Tabs--modal"
        index={selectedTab}
        onChange={(index: number) => {
          if (index === 1) {
            debouncedSetSearchTerm.cancel()
            setQuery('')
            setSearchTerm('')
          }
          setSelectedTab(index)
        }}
      >
        <Tab label="All tracks">
          <Row columnsSize={[3]}>
            <div>
              <Input
                icon="search"
                placeholder="Search"
                value={query}
                onChange={updateSearchQuery}
              />
            </div>
          </Row>
          {tracks && renderContent(tracks.all)}
        </Tab>
        <Tab label="Featured tracks">
          {tracks && renderContent(tracks.featured)}
        </Tab>
      </Tabs>
    </PageContainer.Section>
  )
}

export default LibrariesLibraryTracks
